import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { SsnMaskDirective, getExistingSsnDigits } from '@k12/directives';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'lib-ssn-field',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CommonModule, ReactiveFormsModule, MatFormFieldModule, MatInputModule, MatButtonModule, MatIconModule, SsnMaskDirective],
  templateUrl: './ssn-field.component.html',
  styleUrls: ['./ssn-field.component.scss'],
})
export class SsnFieldComponent implements OnInit, OnDestroy {
  @ViewChild('maskedInput') maskedInput?: ElementRef<HTMLInputElement>;

  @Input({ required: true }) control!: FormControl<string | null>;
  @Input() id = 'ssn';
  @Input() ariaErrorId: string | null = null;
  @Input() editable = true;
  @Input() eyeButtonClass: string | null = null;
  @Input() placeholder = 'XXX-XX-1234';
  @Input() ssnFieldClass: string | null = null;
  @Input() ssnMatFieldClass = 'ssn-field';
  @Input() allowFullSsnEdit = false;

  showFullSsn = false;
  lockEyeToggleDuringFullEdit = false;
  private suppressAutoUnmaskOnNextMaskedFocus = false;

  private fullSsnDigits = '';
  private lastKnownFullSsnDigits = '';
  private initialPrefix5 = '';
  private isEditMode = false;
  private isSyncing = false;

  private readonly destroy$ = new Subject<void>();

  // Expose the control's value as a string of digits for display purposes
  get fullSsnDisplay(): string {
    if (this.fullSsnDigits.length !== 9) return '';
    return `${this.fullSsnDigits.slice(0, 3)}-${this.fullSsnDigits.slice(3, 5)}-${this.fullSsnDigits.slice(5)}`;
  }

  // Expose whether the component is in masked edit mode (for use in the template)
  get useMaskedEditMode(): boolean {
    return this.isEditMode;
  }

  // Expose whether the component can toggle visibility of the full SSN (for use in the template)
  get canToggleVisibility(): boolean {
    if (!this.editable) return false;
    return this.lastKnownFullSsnDigits.length === 9 || getExistingSsnDigits(this.control?.value).length === 9;
  }

  // Expose the full SSN in a display-friendly format (XXX-XX-XXXX) for use in the template when the full SSN is revealed
  get revealSsnDisplay(): string {
    const digits = this.lastKnownFullSsnDigits || this.fullSsnDigits;
    if (digits.length !== 9) return '';
    return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
  }

  ngOnInit(): void {
    const initialDigits = getExistingSsnDigits(this.control?.value);
    // If the initial value is a full 9-digit SSN, we enter edit mode and store the first 5 digits for later merging with the last 4.
    if (initialDigits.length === 9) {
      this.isEditMode = true;
      this.initialPrefix5 = initialDigits.slice(0, 5);
      this.fullSsnDigits = initialDigits;
      this.lastKnownFullSsnDigits = initialDigits;
      this.writeIfChanged(initialDigits);
    } else {
      this.isEditMode = false;
      this.initialPrefix5 = '';
    }

    this.control.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      if (this.isSyncing) return;
      this.normalizeControlValue(value);
    });
  }

  // Visibility of full SSN when eye icon toggle button is clicked
  toggleVisibility(): void {
    if (!this.canToggleVisibility) return;
    // Prevent toggling visibility during full SSN edit mode if the lock is active
    if (this.allowFullSsnEdit && this.lockEyeToggleDuringFullEdit) return;

    if (!this.showFullSsn) {
      const digits = getExistingSsnDigits(this.control?.value);
      if (digits.length === 9) {
        this.fullSsnDigits = digits;
        this.lastKnownFullSsnDigits = digits;
      }
    }

    this.showFullSsn = !this.showFullSsn;

    if (!this.showFullSsn) {
      // When toggling back to masked view, we want to ensure that the input field displays the masked format (XXX-XX-XXXX) 
      // and that the caret is positioned at the end of the input.
      if (this.allowFullSsnEdit) {
        this.suppressAutoUnmaskOnNextMaskedFocus = true;
      }
      this.applyMaskedDisplay();
    }
  }

  // When the masked input field is focused, we reveal the full SSN for editing if allowed
  onMaskedFocusForFullEdit(): void {
    if (!this.allowFullSsnEdit) return;
    if (this.suppressAutoUnmaskOnNextMaskedFocus) {
      this.suppressAutoUnmaskOnNextMaskedFocus = false;
      return;
    }
    this.showFullSsn = true;
    this.lockEyeToggleDuringFullEdit = true;
  }

  // When the masked input field loses focus, we unlock the eye toggle to allow visibility toggling again
  onUnmaskedBlurForFullEdit(): void {
    if (!this.allowFullSsnEdit) return;
    this.lockEyeToggleDuringFullEdit = false;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Applies the masked display (XXX-XX-XXXX) to the input field when the full SSN is hidden
  private applyMaskedDisplay(): void {
    if (!this.maskedInput?.nativeElement || this.showFullSsn) return;

    requestAnimationFrame(() => {
      const input = this.maskedInput?.nativeElement;
      if (!input) return;

      const digits = getExistingSsnDigits(this.control.value);
      input.value =
        digits.length === 9 ? `XXX-XX-${digits.slice(-4)}` : input.value;
    });
  }

  // Normalizes the control value by extracting digits and handling full SSN or partial edits
  private normalizeControlValue(value: string | null): void {
    const digits = getExistingSsnDigits(value);

    // If the user is editing the full SSN and has entered a masked value (with 'X's), 
    // we merge it with the last known full SSN digits to preserve any previously entered digits.
    if (this.allowFullSsnEdit && this.isEditMode && typeof value === 'string' && this.lastKnownFullSsnDigits.length === 9) {
      const normalizedValue = value.toUpperCase().includes('X')
        ? this.mergeMaskedInputWithExistingDigits(value, this.lastKnownFullSsnDigits)
        : digits.slice(0, 9);

      this.fullSsnDigits = normalizedValue;
      this.lastKnownFullSsnDigits = normalizedValue;
      this.lockEyeToggleDuringFullEdit = normalizedValue.length !== 9;
      this.writeIfChanged(normalizedValue);
      return;
    }

    // If the user has entered a full 9-digit SSN, we store it and update the control value
    if (digits.length === 9) {
      this.fullSsnDigits = digits;
      this.lastKnownFullSsnDigits = digits;
      // If full SSN edit is allowed, we unlock the eye toggle to allow visibility toggling again
      if (this.allowFullSsnEdit) {
        this.lockEyeToggleDuringFullEdit = false;
      }
      this.writeIfChanged(digits);
      return;
    }

    // Enrollment PC form field mode: keep full SSN editable (all 9 digits),
    // even while visually masked when eye icon is off.
    if (this.allowFullSsnEdit) {
      this.lockEyeToggleDuringFullEdit = true;
      this.writeIfChanged(digits);
      return;
    }

    // If the user is in edit mode and has entered the last 4 digits, we merge it with the initial prefix to form a full SSN
    if (this.isEditMode && digits.length === 4 && this.initialPrefix5.length === 5) {
      const merged = `${this.initialPrefix5}${digits}`;
      this.fullSsnDigits = merged;
      this.lastKnownFullSsnDigits = merged;
      this.writeIfChanged(merged);
      return;
    }

    // do not clear lastKnownFullSsnDigits on partial/error states
    if (!this.isEditMode) {
      this.writeIfChanged(digits);
    }
  }

  // Merges the masked input value with the existing full SSN digits to preserve any previously entered digits in the masked positions
  private mergeMaskedInputWithExistingDigits(maskedValue: string, existingDigits: string): string {
    if (existingDigits.length !== 9) {
      return getExistingSsnDigits(maskedValue).slice(0, 9);
    }

    // If the masked value is shorter than 11 characters (the length of a fully masked SSN with dashes), 
    // we pad it to ensure we can correctly map the positions of the digits and 'X's.
    const paddedValue = (maskedValue ?? '').toUpperCase().padEnd(11, ' ');
    const output = existingDigits.split('');
    const positions = [0, 1, 2, 4, 5, 7, 8, 9, 10];

    positions.forEach((position, index) => {
      const char = paddedValue[position];
      if (/\d/.test(char)) {
        output[index] = char;
      }
    });

    return output.join('');
  }

  // Writes the given value to the control if it has changed, preventing unnecessary value change events
  private writeIfChanged(next: string): void {
    if (this.control.value === next) return;
    this.isSyncing = true;
    this.control.setValue(next, { emitEvent: false });
    this.isSyncing = false;
  }
}